import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, MapPin, Users } from "lucide-react";
import type { OverviewUpcomingRetreat } from "../../../types/dashboard";
import { capitalizeLabel, dateParts, formatRetreatDates } from "./format";
import styles from "../Dashboard.module.scss";

export default function UpcomingRetreatsPanel({
  retreats,
}: {
  retreats: OverviewUpcomingRetreat[];
}): JSX.Element {
  return (
    <section className={styles.panel}>
      <div className={styles.panelHeader}>
        <div className={styles.panelTitleWrap}>
          <span className={styles.panelGlyph}>
            <CalendarDays size={16} />
          </span>
          <div>
            <h3 className={styles.panelTitle}>Upcoming Retreats</h3>
            <p className={styles.panelKicker}>Registration open and on the calendar</p>
          </div>
        </div>
        <Link to="/manageRetreat" className={styles.panelLink}>
          View All <ArrowRight size={14} />
        </Link>
      </div>
      {retreats.length === 0 ? (
        <div className={styles.empty}>
          <CalendarDays size={26} color="#285d8f" />
          <p className="mb-0">No upcoming retreats with open registration.</p>
        </div>
      ) : (
        <div className={styles.list}>
          {retreats.map((retreat) => {
            const location =
              retreat.parishLocation || capitalizeLabel(retreat.location) || "";
            const language = retreat.language
              ? capitalizeLabel(retreat.language)
              : "";
            const stamp = dateParts(retreat.dateFrom);
            return (
              <Link
                key={retreat.id}
                to={`/retreatRoster?retreatId=${retreat.id}`}
                className={styles.retreatRow}
              >
                <span className={styles.dateBlock}>
                  <span className={styles.dateMonth}>{stamp.month}</span>
                  <span className={styles.dateDay}>{stamp.day}</span>
                </span>
                <div className={styles.rowBody}>
                  <p className={styles.rowTitle}>
                    {retreat.parishName || retreat.title || `Retreat ${retreat.id}`}
                  </p>
                  <p className={styles.rowMeta}>
                    <span>
                      <CalendarDays size={12} />
                      {formatRetreatDates(retreat.dateFrom, retreat.dateTo)}
                    </span>
                    {location ? (
                      <span>
                        <MapPin size={12} />
                        {location}
                      </span>
                    ) : null}
                    {language ? <span>{language}</span> : null}
                  </p>
                </div>
                <div className={styles.rowStats}>
                  <span className={styles.counts}>
                    <Users size={12} /> {retreat.countRetreatant || 0} retreatants ·{" "}
                    {retreat.countTeam || 0} team
                  </span>
                  {retreat.waitList ? (
                    <span className={`${styles.badge} ${styles.badgeWarn}`}>Waitlist</span>
                  ) : (
                    <span className={`${styles.badge} ${styles.badgeOk}`}>Open</span>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}
